import { Api } from "../axios-config";

export interface IAnalysisQueueItem {
	id: string;
	external_id?: string;
	analysis_id?: string;
	partner_name?: string;
	partner_endpoint?: string;
	request_payload?: Record<string, unknown>;
	response_payload?: Record<string, unknown>;
	queue_document?: string;
	queue_client_id?: string;
	queue_street?: string;
	queue_number?: string;
	queue_complement?: string;
	queue_rent_amount?: string;
	document?: string;
	status: string;
	attempt_count?: number;
	number_request?: number;
	max_attempts?: number;
	partner_status?: string;
	partner_reason?: string;
	partner_decision?: string;
	error_step?: string;
	last_error?: string;
	serasa_error?: boolean;
	serasa_reprocess_count?: number;
	last_serasa_reprocess_at?: string | null;
	next_retry_at?: string | null;
	timeout_ms?: number;
	reanalysis?: number;
	created_at?: string;
	updated_at?: string;
}

export interface IAnalysisQueueResponse {
	data: IAnalysisQueueItem[];
	total: number;
	page: number;
	limit: number;
}

export interface IAnalysisQueueFilters {
	page?: number;
	limit?: number;
	status?: string;
	partner_name?: string;
	analysis_id?: string;
	document?: string;
	dataini?: string;
	datafim?: string;
	error_step?: string;
	serasa_error_only?: boolean;
}

const toParams = (f: IAnalysisQueueFilters): string => {
	const p = new URLSearchParams();
	if (f.page)              p.set("page",              String(f.page));
	if (f.limit)             p.set("limit",             String(f.limit));
	if (f.status)            p.set("status",            f.status);
	if (f.partner_name)      p.set("partner_name",      f.partner_name);
	if (f.analysis_id)       p.set("analysis_id",       f.analysis_id);
	if (f.document)          p.set("document",          f.document);
	if (f.dataini)           p.set("dataini",           f.dataini);
	if (f.datafim)           p.set("datafim",           f.datafim);
	if (f.error_step)        p.set("error_step",        f.error_step);
	if (f.serasa_error_only) p.set("serasa_error_only", "true");
	return p.toString();
};

const normalize = (raw: unknown): IAnalysisQueueResponse => {
	if (Array.isArray(raw))
		return { data: raw as IAnalysisQueueItem[], total: (raw as unknown[]).length, page: 1, limit: (raw as unknown[]).length };
	const d = raw as Record<string, unknown>;
	return {
		data:  (d.data ?? d.items ?? []) as IAnalysisQueueItem[],
		total: Number(d.total ?? d.count ?? 0),
		page:  Number(d.page  ?? 1),
		limit: Number(d.limit ?? 20),
	};
};

const listQueue = async (filters: IAnalysisQueueFilters = {}): Promise<IAnalysisQueueResponse | Error> => {
	try {
		const { data } = await Api().get(`/v2/analysis-queue?${toParams(filters)}`);
		return normalize(data);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao listar fila de análise.");
	}
};

const listSerasaErrors = async (filters: Omit<IAnalysisQueueFilters, "error_step" | "serasa_error_only"> = {}): Promise<IAnalysisQueueResponse | Error> => {
	try {
		const { data } = await Api().get(`/v2/analysis-queue/errors/serasa?${toParams(filters)}`);
		return normalize(data);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao listar erros Serasa.");
	}
};

const reprocessSerasaErrors = async (): Promise<void | Error> => {
	try {
		await Api().post("/v2/analysis-queue/errors/serasa/reprocess");
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao reprocessar erros Serasa.");
	}
};

const retrySerasaItem = async (id: string): Promise<void | Error> => {
	try {
		await Api().post(`/v2/analysis-queue/${id}/retry-serasa`);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao reprocessar item Serasa.");
	}
};

const stopSerasaRetryItem = async (id: string): Promise<void | Error> => {
	try {
		await Api().post(`/v2/analysis-queue/${id}/stop-serasa-retry`);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao parar retry Serasa.");
	}
};

const restartItem = async (id: string): Promise<void | Error> => {
	try {
		await Api().post(`/v2/analysis-queue/${id}/restart`);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao reiniciar item.");
	}
};

const stopItem = async (id: string): Promise<void | Error> => {
	try {
		await Api().post(`/v2/analysis-queue/${id}/stop`);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao parar item.");
	}
};

const deleteItem = async (id: string): Promise<void | Error> => {
	try {
		await Api().delete(`/v2/analysis-queue/${id}`);
	} catch (error) {
		return new Error((error as { message: string }).message || "Erro ao remover item da fila.");
	}
};

export const AnalysisQueueService = {
	listQueue,
	listSerasaErrors,
	reprocessSerasaErrors,
	retrySerasaItem,
	stopSerasaRetryItem,
	restartItem,
	stopItem,
	deleteItem,
};
